import type { DownloadTableFormat, DownloadableTableData } from "@cynthai/ui";
import dayjs from "dayjs";

import { apiClient } from "@/core/lib/api-client";
import { isAbortError, UsageLimitExceededError } from "@/core/lib/api-errors";
import { config } from "@/core/lib/app-config";
import {
  getBlobName,
  getUploadedFileName,
  isViewBacked,
} from "@/core/lib/file-utils";
import { formatDate } from "@/core/lib/format-date";
import { addToast, sonnerToast } from "@/core/lib/toast";
import type {
  ConversationDatasource,
  Datasource,
  DocumentAnalysisCompleteEvent,
  DocumentAnalysisRecordEvent,
  DocumentAnalysisRunNumberEvent,
  DocumentAnalysisSearchData,
  DocumentAnalysisUsageLimitEvent,
  ExportAgbwOutputData,
  ExportDataData,
  MessageMention,
  PowerBiReportInput,
  NewMessageStreamingData,
  NewMessageStreamingResponse,
  ChatUsageLimitEvent,
} from "@/openapi";
import {
  exportClassificationResultsExcel,
  exportValidationResults,
} from "@/openapi/sdk.gen";
import { m } from "@/paraglide/messages";

type StreamFormat = "sse" | "ndjson";

interface StreamReaderOptions<T> {
  reader: ReadableStreamDefaultReader<Uint8Array>;
  format: StreamFormat;
  onData: (data: T) => void;
}

async function readStream<T>({
  reader,
  format,
  onData,
}: StreamReaderOptions<T>): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = "";
  const delimiter = format === "sse" ? "\n\n" : "\n";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split(delimiter);
    buffer = chunks.pop() || "";

    for (const chunk of chunks) {
      const parsed = parseChunk<T>(chunk, format);
      if (parsed !== null) onData(parsed);
    }
  }

  // Process remaining buffer
  if (buffer.trim()) {
    const parsed = parseChunk<T>(buffer, format);
    if (parsed !== null) onData(parsed);
  }
}

function parseChunk<T>(chunk: string, format: StreamFormat): T | null {
  const trimmed = chunk.trim();
  if (!trimmed) return null;

  const content =
    format === "sse" && trimmed.startsWith("data:")
      ? trimmed.slice(5).trim()
      : trimmed;

  if (!content) return null;

  try {
    return JSON.parse(content) as T;
  } catch {
    console.warn(`Failed to parse stream chunk:`, content);
    return null;
  }
}

// the client's own url, path and query handling, so a stream goes through the
// same interceptors (token refresh, 401 replay, error toasts) as any request
interface StreamRequest {
  url: string;
  path?: Record<string, string>;
  query?: Record<string, unknown>;
  body: unknown;
  accept: string;
}

interface CreateStreamOptions<T> {
  request: StreamRequest;
  format: StreamFormat;
  onData: (data: T) => void;
  onError?: (error: Error) => void;
  onFinish?: () => void;
  signal?: AbortSignal;
}

function createStream<T>(options: CreateStreamOptions<T>): StreamController {
  const { request, format, onData, onError, onFinish, signal } = options;

  const controller = new AbortController();
  const combinedSignal = signal
    ? AbortSignal.any([signal, controller.signal])
    : controller.signal;

  const done = (async () => {
    try {
      const { data } = await apiClient.post<
        ReadableStream<Uint8Array>,
        unknown,
        true
      >({
        url: request.url,
        path: request.path,
        query: request.query,
        body: request.body,
        headers: { Accept: request.accept },
        parseAs: "stream",
        signal: combinedSignal,
        throwOnError: true,
      });

      await readStream<T>({
        reader: data.getReader(),
        format,
        onData,
      });

      onFinish?.();
    } catch (error) {
      if (isAbortError(error)) return;

      const err = error instanceof Error ? error : new Error(String(error));
      onError?.(err);
    }
  })();

  return {
    abort: () => controller.abort(),
    done,
  };
}

export interface StreamController {
  /** Abort the streaming request */
  abort: () => void;
  /** Promise that resolves when the stream is done */
  done: Promise<void>;
}

function isChatUsageLimitEvent(
  event: NewMessageStreamingResponse,
): event is ChatUsageLimitEvent {
  return "event_type" in event && event.event_type === "usage_limit_exceeded";
}

function isDocumentAnalysisUsageLimitEvent(
  event: DocumentAnalysisStreamEvent,
): event is DocumentAnalysisUsageLimitEvent {
  return "type" in event && event.type === "usage_limit_exceeded";
}

export interface SendMessageOptions {
  /** Conversation ID to send the message to */
  conversationId: string;
  /** Message text content */
  message: string;
  /** Optional datasources to include */
  datasources?: { id: string }[];
  /** Optional collection IDs to include */
  collectionIds?: string[];
  /** Optional project IDs mentioned in the message, expanded server-side */
  projectIds?: string[];
  /** Mention spans, kept so the conversation history can render them as chips */
  mentions?: MessageMention[];
  /** Per-turn prompt context, shown with the answer but never as the user's message */
  extraContext?: string;
  /** The embedded Power BI report, whose semantic model the agent's tools read */
  powerbiReport?: PowerBiReportInput;
  /** Callback for each message chunk received */
  onMessage?: (message: NewMessageStreamingResponse) => void;
  /** Callback for errors */
  onError?: (error: Error) => void;
  /** Optional AbortSignal to cancel the request */
  signal?: AbortSignal;
}

// collected at send time from whatever sits next to the chat
export type TurnContext = Pick<
  SendMessageOptions,
  "extraContext" | "powerbiReport"
>;

export function sendMessage(options: SendMessageOptions): StreamController {
  const {
    conversationId,
    message,
    datasources,
    collectionIds,
    projectIds,
    mentions,
    extraContext,
    powerbiReport,
    onMessage,
    onError,
    signal,
  } = options;

  const query: NewMessageStreamingData["query"] = {
    conversation_id: conversationId,
    datasource_ids: datasources?.map(({ id }) => id),
    collection_ids: collectionIds,
    project_ids: projectIds,
  };

  const body: NewMessageStreamingData["body"] = { text: message };
  if (mentions?.length) body.mentions = mentions;
  if (extraContext) body.extra_context = extraContext;
  if (powerbiReport) body.powerbi_report = powerbiReport;

  return createStream<NewMessageStreamingResponse>({
    request: {
      url: "/tracker/message/stream",
      query,
      body,
      accept: "text/event-stream",
    },
    format: "sse",
    onData: (msg) => {
      if (isChatUsageLimitEvent(msg)) {
        throw new UsageLimitExceededError();
      }

      onMessage?.(msg);
    },
    onError,
    signal,
  });
}

export const downloadAuditReport = (data: {
  organizationId: string;
  executionId: string;
}) => {
  const params = new URLSearchParams({
    execution_url: window.location.href,
  });

  return (
    config.backendUrl +
    `/organization/${data.organizationId}/workflow/executions/${data.executionId}/report?` +
    params
  );
};

export const triggerBrowserDownload = (blob: Blob, filename: string): void => {
  const objectUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  try {
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
  } finally {
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  }
};

// stored files go straight to the browser's download manager, which streams
// them to disk with its own progress; fetching them into a blob first would
// hold the whole file in memory and show nothing until the last byte arrived.
// the api is served from the app's origin, so `download` is honoured and a
// failed response cannot navigate the app away
const startFileDownload = (url: string, filename: string): void => {
  const a = document.createElement("a");
  try {
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
  } finally {
    a.remove();
  }

  addToast({
    title: m.file_download_started(),
    description: m.file_download_started_description(),
  });
};

export const downloadOriginalFile = (
  datasource: Datasource | ConversationDatasource,
) => {
  const blobName = getBlobName(datasource);
  const url = getDatasourceFileUrl(datasource);
  if (!blobName || !url) return;

  startFileDownload(url, getUploadedFileName(blobName));
};

export const downloadDatasource = (
  datasource: Datasource | ConversationDatasource,
) => {
  if (!datasource.origin || !isViewBacked(datasource)) {
    downloadOriginalFile(datasource);
    return;
  }

  startFileDownload(
    apiClient.buildUrl({
      url: "/organization/{org_id}/datasource/{datasource_id}/export",
      path: {
        org_id: datasource.origin.organization_id,
        datasource_id: datasource.id,
      },
    }),
    `${datasource.name}.csv`,
  );
};

export const downloadAuditFile = (options: {
  organizationId: string;
  projectId: string;
  filename: string;
  folder: "fa" | "hr";
  bookingYear?: number;
}) => {
  const params = new URLSearchParams({ folder: options.folder });
  if (options.bookingYear !== undefined) {
    params.set("booking_year", String(options.bookingYear));
  }

  startFileDownload(
    `${config.backendUrl}/organization/${options.organizationId}/project/${options.projectId}/audit-files/download/${encodeURIComponent(options.filename)}?${params}`,
    options.filename,
  );
};

export const downloadFile = async (
  extractedData: ExportDataData["body"],
  format: "csv" | "excel" | "json",
  showToast: boolean = true,
) => {
  // the generated exportData types the json rows; asked for a blob, the body
  // is the file itself
  const promise = apiClient.post<Blob, unknown, true>({
    url: "/data/export",
    query: { to: format },
    body: extractedData,
    parseAs: "blob",
    throwOnError: true,
  });

  if (showToast) {
    sonnerToast.promise(promise, {
      loading: m.downloading_file(),
      error: {
        message: m.file_download_failed(),
        description: m.please_try_again(),
      },
    });
  }

  try {
    const res = await promise;
    const contentDisposition = res.response.headers.get("content-disposition");

    const extension = {
      csv: "csv",
      excel: "xlsx",
      json: "json",
    }[format];

    // header value looks like `attachment; filename="x.xlsx"; ...`
    const headerFilename = contentDisposition
      ?.split("filename=")[1]
      ?.split(";")[0]
      ?.replaceAll('"', "")
      .trim();
    const filename =
      headerFilename ||
      `data_export_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.${extension}`;

    triggerBrowserDownload(res.data, filename);
  } catch (error) {
    throw new Error("File download failed", { cause: error });
  }
};

type AgbwSection = "overview" | "payments" | "wwft" | "miscellaneous";

export const buildDrilldownExportUrl = (options: {
  section: AgbwSection;
  slug: string;
  orgId: string;
  clientId: string;
  bookingYear: number;
  run: number;
  filters?: Record<string, string | number | undefined>;
}): string => {
  const { section, slug, orgId, clientId, bookingYear, run, filters } = options;
  const params = new URLSearchParams({
    booking_year: String(bookingYear),
    run: String(run),
  });
  if (filters) {
    for (const [k, v] of Object.entries(filters)) {
      if (v != null) params.append(k, String(v));
    }
  }
  return `${config.backendUrl}/organization/${encodeURIComponent(orgId)}/audit/${encodeURIComponent(clientId)}/agbw/output/${encodeURIComponent(section)}/${encodeURIComponent(slug)}/drilldown/export?${params.toString()}`;
};

export const buildExportAgbwResultsUrl = (options: {
  orgId: string;
  clientId: string;
  outputFormat: NonNullable<ExportAgbwOutputData["query"]>["output_format"];
  worksheets?: NonNullable<
    NonNullable<ExportAgbwOutputData["query"]>["worksheets"]
  >;
  locale: string;
}) => {
  const { orgId, clientId, outputFormat, worksheets, locale } = options;

  const params = new URLSearchParams();

  params.append("locale", locale);
  params.append("url", window.location.href);

  if (outputFormat) params.append("output_format", outputFormat);
  if (worksheets) {
    worksheets.forEach((worksheet) => {
      params.append("worksheets", worksheet);
    });
  }

  const url = apiClient.buildUrl({
    url: "/organization/{org_id}/audit/{project_id}/agbw/output/export",
    path: { org_id: orgId, project_id: clientId },
  });

  return url + (params.toString() ? `?${params.toString()}` : "");
};

export const buildExportDocumentAnalysisUrl = (options: {
  orgId: string;
  /** omitted for personal runs, which have no project */
  clientId?: string;
  templateId: string;
  datasourceId: string;
  runNumber: number;
  format: "excel" | "pdf";
}) => {
  const { orgId, clientId, templateId, datasourceId, runNumber, format } =
    options;

  const params = new URLSearchParams();
  if (clientId) params.append("project_id", clientId);
  params.append("template_id", templateId);
  params.append("datasource_id", datasourceId);

  const suffix = format === "pdf" ? "/pdf" : "";
  const url = apiClient.buildUrl({
    url: `/organization/{org_id}/document-analysis/run/{run_number}/results/export${suffix}`,
    path: { org_id: orgId, run_number: runNumber },
  });

  return url + `?${params.toString()}`;
};

// datasource originals are only served here: /file refuses them because it
// checks nothing beyond the organization
export const getDatasourceFileUrl = (
  datasource: Datasource | ConversationDatasource | null | undefined,
): string | null => {
  if (!datasource?.origin || !getBlobName(datasource)) return null;

  return apiClient.buildUrl({
    url: "/organization/{org_id}/datasource/{datasource_id}/file",
    path: {
      org_id: datasource.origin.organization_id,
      datasource_id: datasource.id,
    },
  });
};

export const getFileUrl = (
  options: { organizationId: string; blobName: string } | { src: string },
) => {
  if ("src" in options) {
    const src = options.src.startsWith("/") ? options.src : `/${options.src}`;
    const path = `/file${src}`;
    return config.backendUrl + path;
  }

  return apiClient.buildUrl({
    url: "/file/{organization_id}/{blob_name}",
    path: {
      organization_id: options.organizationId,
      blob_name: options.blobName,
    },
  });
};

export const downloadValidationResults = async (options: {
  clientId: string;
  organizationId: string;
  includeIrrelevant: boolean;
}) => {
  const response = await exportValidationResults({
    path: {
      project_id: options.clientId,
      org_id: options.organizationId,
    },
    query: { include_irrelevant: options.includeIrrelevant },
    parseAs: "blob",
  });

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([JSON.stringify(response.data)], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

  const date = formatDate(new Date().toISOString(), "file_name");
  triggerBrowserDownload(blob, `validation_results_${date}.xlsx`);

  addToast({
    severity: "success",
    title: m.validation_test_export_success(),
    duration: 5_000,
  });
};

export const downloadClassificationResults = async (options: {
  clientId: string;
  organizationId: string;
  templateId: string;
}) => {
  const response = await exportClassificationResultsExcel({
    path: {
      project_id: options.clientId,
      org_id: options.organizationId,
      template_id: options.templateId,
    },
    parseAs: "blob",
  });

  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([JSON.stringify(response.data)], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

  const date = formatDate(new Date().toISOString(), "file_name");
  triggerBrowserDownload(blob, `classification_results_${date}.xlsx`);

  addToast({
    severity: "success",
    title: m.classification_export_success(),
    duration: 5_000,
  });
};

export const copyDatasourceToClipboard = async (
  extractedData: ExportDataData["body"],
) => {
  try {
    const res = await apiClient.post<string, unknown, true>({
      url: "/data/export",
      query: { to: "csv" },
      body: extractedData,
      parseAs: "text",
      throwOnError: true,
    });

    await navigator.clipboard.writeText(res.data);

    addToast({
      title: m.toast_copied_to_clipboard(),
      severity: "success",
    });
  } catch (error) {
    // the clipboard write itself can fail (permissions, focus), not just
    // the export request: both deserve feedback
    addToast({
      title: m.toast_failed_to_copy_datasource_to_clipboard(),
      severity: "danger",
      error,
    });
    throw error;
  }
};

export const exportTableData = (
  format: DownloadTableFormat,
  data: DownloadableTableData,
) => {
  // fire-and-forget: failures already surface as toasts inside the helpers
  if (format === "clipboard") {
    copyDatasourceToClipboard(data).catch(() => undefined);
  } else {
    downloadFile(data, format).catch(() => undefined);
  }
};

type DocumentAnalysisStreamEvent =
  | DocumentAnalysisRunNumberEvent
  | DocumentAnalysisRecordEvent
  | DocumentAnalysisCompleteEvent
  | DocumentAnalysisUsageLimitEvent;

type DocumentAnalysisStreamBody = Omit<
  DocumentAnalysisSearchData["body"],
  "selected_conditions"
> & {
  selected_conditions?: string[] | null;
};

export interface StreamDocumentAnalysisOptions {
  path: DocumentAnalysisSearchData["path"];
  query?: Exclude<DocumentAnalysisSearchData["query"], undefined>;
  body: DocumentAnalysisStreamBody;
  onRunNumber?: (event: DocumentAnalysisRunNumberEvent) => void;
  onRecord?: (event: DocumentAnalysisRecordEvent) => void;
  onComplete?: (event: DocumentAnalysisCompleteEvent) => void;
  onError?: (error: Error) => void;
  onFinish?: () => void;
  signal?: AbortSignal;
}

export function streamDocumentAnalysisSearch(
  options: StreamDocumentAnalysisOptions,
): StreamController {
  const {
    path,
    query,
    body,
    onRunNumber,
    onRecord,
    onComplete,
    onError,
    onFinish,
    signal,
  } = options;
  const requestBody: DocumentAnalysisSearchData["body"] = {
    ...body,
    selected_conditions:
      body.selected_conditions === undefined ||
      body.selected_conditions === null
        ? body.selected_conditions
        : body.selected_conditions.map((condition) => condition.split(",")),
  };
  return createStream<DocumentAnalysisStreamEvent>({
    request: {
      url: "/organization/{org_id}/document-analysis/{template_id}/search",
      path,
      query,
      body: requestBody,
      accept: "application/x-ndjson",
    },
    format: "ndjson",
    onData: (event) => {
      if (event.type === "run_number") {
        onRunNumber?.(event);
      } else if (event.type === "record") {
        onRecord?.(event);
      } else if (event.type === "complete") {
        onComplete?.(event);
      } else if (isDocumentAnalysisUsageLimitEvent(event)) {
        throw new UsageLimitExceededError();
      }
    },
    onError: (error) => {
      if (error instanceof UsageLimitExceededError) {
        addToast({
          severity: "danger",
          title: m.toast_ai_usage_limit_exceeded(),
          error,
        });
      }
      onError?.(error);
    },
    onFinish,
    signal,
  });
}
